import { useCallback, useEffect, useState } from 'react';
import { registerService } from '@/services/api/registerService/registerService';
import { useTimeout } from '@/hooks/general/useTimeout';
import { logger } from '@/utils/logger/logger';
import * as Sentry from '@sentry/nextjs';

export const REGISTRATION_RELEVANT_ID_CLIENT_ID = 'BE182609-6057-4EDE-8757-E504FEE7783D';
export const RELEVANT_ID_SCRIPT_LOADING_TIMEOUT = 10000;

export const useSendRelevantIdData = (
    registrationId?: string | null,
    relevantIdRequestId?: string,
    relevantIdSurveyId?: string,
    wasRelevantIdScriptLoaded?: boolean,
    isDataLoading?: boolean,
) => {
    const [isRelevantIdDataLoading, setIsRelevantIdDataLoading] = useState(true);
    const [hasBeenExecuted, setHasBeenExecuted] = useState(false);
    const [relevantIdData, setRelevantIdData] = useState<Record<string, unknown>>();

    const initTime = Date.now();

    const getTimeDifferenceInSecs = (initTime: number) =>
        ((Date.now() - initTime) / 1000).toFixed(2);

    useTimeout(
        () => {
            if (!hasBeenExecuted) {
                setIsRelevantIdDataLoading(false);
                setHasBeenExecuted(true);

                const scriptNotLoadedErrorMessage = `Attempt to proceed relevantId data is failed as api's script not being loaded within ${
                    RELEVANT_ID_SCRIPT_LOADING_TIMEOUT / 1000
                } seconds`;

                logger.error(scriptNotLoadedErrorMessage);
                Sentry.captureException(new Error(scriptNotLoadedErrorMessage), {
                    extra: { registrationId, relevantIdRequestId },
                });
            }
        },
        RELEVANT_ID_SCRIPT_LOADING_TIMEOUT,
        hasBeenExecuted,
    );

    const sendRelevantIdData = useCallback(
        async (responseData?: Record<string, string>) => {
            if (hasBeenExecuted) {
                return;
            }
            setHasBeenExecuted(true);
            if (!registrationId || !relevantIdRequestId || !relevantIdSurveyId || !responseData) {
                Sentry.captureException(
                    new Error('Attempt to send relevantId data with one of params missed'),
                    {
                        extra: {
                            registrationId,
                            relevantIdRequestId,
                            relevantIdSurveyId,
                            responseData,
                        },
                    },
                );
                setIsRelevantIdDataLoading(false);
                return;
            }

            const secsBeforeScriptLoaded = getTimeDifferenceInSecs(initTime);
            logger.info(`script loaded in ${secsBeforeScriptLoaded} secs`);

            const relevantIdData = {
                ...responseData,
                rvid_response_timespent: secsBeforeScriptLoaded,
            };

            try {
                await registerService.postRelevantIdData(
                    registrationId,
                    relevantIdRequestId,
                    relevantIdData,
                );
            } catch (err) {
                Sentry.captureException(err, {
                    extra: {
                        registrationId,
                        relevantIdRequestId,
                        relevantIdSurveyId,
                        relevantIdData,
                    },
                });
            } finally {
                setIsRelevantIdDataLoading(false);
            }
        },
        [hasBeenExecuted, initTime, registrationId, relevantIdRequestId, relevantIdSurveyId],
    );

    useEffect(() => {
        if (!isDataLoading && !relevantIdRequestId) {
            setIsRelevantIdDataLoading(false);
            setHasBeenExecuted(true);
            return;
        }

        if (!hasBeenExecuted && wasRelevantIdScriptLoaded && window.relevantID) {
            window.relevantID.callService(
                {
                    ClientID: REGISTRATION_RELEVANT_ID_CLIENT_ID,
                    SurveyID: relevantIdSurveyId,
                    PanelistID: registrationId,
                },
                async (responseData) => {
                    setRelevantIdData(responseData);
                    await sendRelevantIdData(responseData);
                },
            );
        }
    }, [
        hasBeenExecuted,
        isDataLoading,
        relevantIdRequestId,
        sendRelevantIdData,
        wasRelevantIdScriptLoaded,
        registrationId,
        relevantIdSurveyId,
    ]);

    return { relevantIdData, isRelevantIdDataLoading, hasBeenExecuted };
};
